<script setup lang="ts">
import { useSpeechRecognition } from '@/composables/useSpeechRecognition';
import type { Editor } from '@tiptap/core';

interface Props {
  editor: Editor | null;
  enabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  enabled: true,
});

const emit = defineEmits<{
  (e: 'error', message: string): void;
  (e: 'start'): void;
  (e: 'end'): void;
}>();

const { isRecording, isSupported, toggleDictation, stopDictation } =
  useSpeechRecognition({
    onError: (message) => emit('error', message),
    onStart: () => emit('start'),
    onEnd: () => emit('end'),
  });

const handleToggle = () => {
  if (!props.enabled || !props.editor) {
    return;
  }
  toggleDictation(props.editor);
};

defineExpose({
  toggleDictation: handleToggle,
  stopDictation,
  isRecording,
});
</script>

<template>
  <button
    v-if="isSupported"
    type="button"
    class="icon-button"
    :class="{
      'is-active': isRecording,
      'is-disabled': !props.enabled || !props.editor,
    }"
    :disabled="!props.enabled || !props.editor"
    :title="
      isRecording ? 'Stop dictation (or press Esc)' : 'Start dictation (Ctrl+D)'
    "
    @click="handleToggle"
  >
    <svg
      v-if="!isRecording"
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
      <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
      <line x1="12" y1="19" x2="12" y2="23"></line>
      <line x1="8" y1="23" x2="16" y2="23"></line>
    </svg>
    <svg
      v-else
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="recording-icon"
    >
      <line x1="1" y1="1" x2="23" y2="23"></line>
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
      <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
      <line x1="12" y1="19" x2="12" y2="23"></line>
      <line x1="8" y1="23" x2="16" y2="23"></line>
    </svg>
  </button>
</template>

<style lang="scss" scoped>
button.icon-button {
  background: white;
  border: 1px solid var(--color-gray-300);
  cursor: pointer;
  padding: var(--space-8);
  color: var(--color-gray-500);
  stroke: var(--color-gray-200);
  border-radius: var(--border-radius-6);

  &:hover {
    background-color: #f9fafb;
  }

  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }

  &.is-active {
    svg {
      width: 18px;
      fill: var(--color-primary);
      stroke: var(--color-primary);
      animation: pulse 1.5s ease-in-out infinite;
    }
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}
</style>
